import type { ReactNode } from "react";
import { useContext, useReducer, useEffect, useRef } from "react";
import { Router } from "@reach/router";
import Board from "./Board";
import { gameStateReducer, initialState } from "src/reducer";
import SettingsForm from "src/components/SettingsForm";
import History from "src/components/History";
import OverlayPanel from "src/components/OverlayPanel";
import { isInNamuaStage, opponent, isValidMove, isGameOver, sumHoles } from "src/core/baoside";
import { KISWAHILI } from "src/core/baogame";
import SelectedHoleContext from "src/components/providers/SelectedHoleProvider";
import GameSettingsContext from "src/components/providers/GameSettingsProvider";
import GameOverModal from "../GameOverModal";
import OrientationContext, { PORTRAIT, type Orientation } from "src/components/providers/OrientationProvider";
import { logGameLost, logGameStarted, logGameWon } from "../../hooks/use-analytics";
import { useFirebaseGame } from "src/hooks/useFirebaseGame";
import { usePlayerPresence } from "src/hooks/usePlayerPresence";
import { useAuth } from "src/contexts/AuthContext";
import { useGameHistory } from "src/hooks/useGameHistory";
import ConnectionStatus from "../ConnectionStatus/ConnectionStatus";
import type { GameFinished, MultiplayerGame, MoveSequence, Settings } from "../../core/types";
import type { PlayAction, ResetGameAction } from "src/reducer";
import type { Theme } from "src/theme";

type Props = {
  path: string;
  gameId?: string;
};

const PLAY_SWAP_INTERVAL = 1500;

// Wrapper components for overlay panels
const SettingsFormOverlay = (props: { path: string }): ReactNode => (
  <OverlayPanel>
    <SettingsForm {...props} />
  </OverlayPanel>
);

const HistoryOverlay = (props: {
  path: string;
  moveHistory: Array<{ action: PlayAction | ResetGameAction; sequence: MoveSequence[] }>;
  playerId?: string;
  game?: MultiplayerGame;
}): ReactNode => (
  <OverlayPanel>
    <History {...props} />
  </OverlayPanel>
);

const MultiplayerBoardContainer = (props: Props): ReactNode => {
  const { gameId } = props;
  const { user } = useAuth();
  const playerId = user ? user.uid : "";

  const { game, loading, error, sendMove, updateGameState } = useFirebaseGame(gameId ?? null);
  const { addGameToHistory } = useGameHistory();
  usePlayerPresence(gameId ?? null, playerId);

  const orientation = useContext<Orientation>(OrientationContext);

  // Track if we're currently processing a move to prevent duplicate sends
  const processingMove = useRef<boolean>(false);

  // Track last received move from Firebase to prevent duplicates
  const lastReceivedMoveCount = useRef<number>(0);

  // Track last sent move to Firebase to prevent duplicates
  const lastSentMoveCount = useRef<number>(0);

  // Initialize reducer - must be called unconditionally (Rules of Hooks)
  // When game is not loaded yet, use placeholder values
  const playerSide = game && playerId === game.host ? 0 : 1;

  const startingGameState = game ? game.gameState : initialState.currentGameState;
  const [sideA, sideB] = startingGameState;
  const store = (64 - sumHoles(sideA) - sumHoles(sideB)) / 2;

  const [state, dispatch] = useReducer(gameStateReducer, {
    ...initialState,
    store,
    currentGameState: startingGameState,
    currentPlayer: game ? game.currentPlayer : 0,
    settings: game ? game.settings : initialState.settings
  });

  // Compute variables (safe even when game is null due to fallback values)
  const currentPlayer = state.currentPlayer;
  const gameState = state.currentGameState;
  const currentSequence = state.currentSequence[state.currentSequenceIndex];
  const currentSequenceHoleIndex = currentSequence ? currentSequence.index : -1;

  const activeIndex = currentSequenceHoleIndex;
  const endSequence =
    state.currentSequence.length > 0 && state.currentSequenceIndex === state.currentSequence.length - 1;

  // In multiplayer, you win if opponent's side is game over, you lose if your side is game over
  const won = gameState && isGameOver(gameState[opponent(playerSide)]);
  const lost = gameState && isGameOver(gameState[playerSide]);
  // Show game over modal when game is over AND we're idle (not animating)
  const idle = endSequence || state.currentSequenceIndex === -1;
  const gameOver = (won || lost) && idle;
  const playbackSpeed = state.settings.playbackSpeed;
  const currentSequenceSide = currentSequence ? currentSequence.side : currentPlayer;

  // Compute game stage from current game state
  const gameStage = gameState && isInNamuaStage(gameState) ? "namua" : "mtaji";

  // Listen to game state changes from Firebase
  useEffect(() => {
    // Only sync from Firebase if we're not currently playing a move sequence
    const isPlayingSequence = state.currentSequence.length > 0 && state.currentSequenceIndex !== -1;

    if (game && !processingMove.current && !isPlayingSequence) {
      // Check if there's a new move from the opponent
      const firebaseMoveCount = game.moveHistory ? game.moveHistory.length : 0;
      const hasNewMove = firebaseMoveCount > lastReceivedMoveCount.current;

      if (hasNewMove && game.moveHistory && game.moveHistory.length > 0) {
        // Get the last move from Firebase
        const lastMove = game.moveHistory[game.moveHistory.length - 1];

        // Only process if it's the opponent's move (not our own echoing back)
        if (lastMove && lastMove.side !== playerSide) {
          // Validate the move before dispatching
          if (
            isValidMove(
              lastMove.index,
              lastMove.direction,
              gameState[lastMove.side],
              gameState[opponent(lastMove.side)]
            )
          ) {
            // Dispatch USER_PLAY to trigger animation (just like AI opponent)
            dispatch({
              type: "USER_PLAY",
              index: lastMove.index,
              direction: lastMove.direction,
              side: lastMove.side
            });
          } else {
            console.warn("Invalid opponent move received from Firebase:", lastMove);
          }
        }
        lastReceivedMoveCount.current = firebaseMoveCount;
      }
    }
  }, [game, gameState, currentPlayer, playerSide, state.currentSequence, state.currentSequenceIndex]);

  // Sync currentPlayer from Firebase after animations complete
  useEffect(() => {
    // Only sync when we're idle (not animating) and Firebase has a different currentPlayer
    const isIdle = state.currentSequenceIndex === -1;

    if (game && isIdle && game.currentPlayer !== currentPlayer) {
      dispatch({
        type: "CHANGE_PLAYER",
        side: game.currentPlayer
      });
    }
  }, [game, currentPlayer, state.currentSequenceIndex]);

  // Handle move animation sequence
  useEffect(() => {
    if (activeIndex !== -1 && !endSequence) {
      const id = setInterval(() => {
        dispatch({ type: "SEQUENCE_STEP_FORWARD" });
      }, playbackSpeed);
      return () => clearInterval(id);
    } else if (endSequence && !gameOver) {
      const id = setTimeout(() => {
        dispatch({
          type: "CHANGE_PLAYER",
          side: opponent(currentPlayer)
        });
      }, PLAY_SWAP_INTERVAL);
      return () => clearTimeout(id);
    }
  }, [activeIndex, currentPlayer, dispatch, endSequence, gameOver, playbackSpeed]);

  // Send move to Firebase immediately when local player makes a move
  useEffect(() => {
    const lastAction = state.moveHistory[state.moveHistory.length - 1];
    const moveCount = state.moveHistory.length;

    // Check if there's a new move from the local player
    if (!lastAction || lastAction.action.type !== "USER_PLAY") {
      return;
    }

    if (lastAction.action.side === playerSide && moveCount > lastSentMoveCount.current && !processingMove.current) {
      processingMove.current = true;
      const playAction = lastAction.action as PlayAction;
      const sequence = lastAction.sequence;

      if (!sequence || sequence.length === 0) {
        processingMove.current = false;
        return;
      }

      // Get final game state from sequence
      const finalGameState = sequence[sequence.length - 1].state;
      const nextPlayer = opponent(playAction.side);

      // Send move and update game state immediately (don't wait for animation to complete)
      Promise.all([
        sendMove(playAction.index, playAction.direction, playAction.side),
        updateGameState(finalGameState, nextPlayer)
      ])
        .then(() => {
          lastSentMoveCount.current = moveCount;
          processingMove.current = false;
        })
        .catch((err) => {
          console.error("Error sending move to Firebase:", err);
          processingMove.current = false;
        });
    }
  }, [state.moveHistory, playerSide, sendMove, updateGameState]);

  // Log game started
  useEffect(() => {
    if (game) {
      logGameStarted({ gameType: game.gameType, startingSide: game.settings.startingSide, expertLevel: "multiplayer" });
    }
  }, [game]);

  // Log game over and save to history
  useEffect(() => {
    if (!gameOver || !game || !gameId) return;

    const numberOfTurns = state.moveHistory.length;
    const gameAttributes: GameFinished = {
      numberOfTurns,
      startingSide: playerSide,
      gameType: game.gameType,
      expertLevel: "multiplayer"
    };

    const result = won ? "won" : "lost";

    if (won) {
      logGameWon(gameAttributes);
    } else {
      logGameLost(gameAttributes);
    }

    // Save to game history
    const opponentId = playerSide === 0 ? game.guest : game.host;
    addGameToHistory({
      gameId,
      gameType: game.gameType,
      mode: "multi",
      result,
      opponent: opponentId || "Unknown",
      numberOfTurns,
      startingSide: playerSide,
      expertLevel: "multiplayer",
      duration: Date.now() - game.createdAt
    }).catch((err) => {
      console.error("Failed to save game to history:", err);
    });
  }, [gameOver, playerSide, state.moveHistory, game, won, gameId, addGameToHistory]);

  // Show loading state while waiting for authentication or Firebase
  // MUST come after all hooks (Rules of Hooks)
  if (!user || loading || !game) {
    return <div css={{ textAlign: "center", padding: 40 }}>{!user ? "Authenticating..." : "Loading game..."}</div>;
  }

  // Show error state
  if (error) {
    return (
      <div css={{ textAlign: "center", padding: 40 }}>
        <div>Error: {error}</div>
        <button onClick={() => (window.location.href = "/multiplayer")}>Back to Lobby</button>
      </div>
    );
  }

  // From this point on, we know `game` exists
  // Only allow moves if it's this player's turn
  const canMove = currentPlayer === playerSide && !processingMove.current;

  const boardContainerStyle = (theme: Theme) => {
    let boardWidth = (theme.holeWidth + 16) * 8 + 10;
    let boardHeight = (theme.holeHeight + 16) * 4 + 10;

    const xOffset = game.gameType === KISWAHILI ? 35 : 0;
    return orientation === PORTRAIT
      ? {
          transformOrigin: "top left",
          transform: `translate(${boardHeight + xOffset}px) rotate(90deg)`,
          height: boardHeight,
          width: boardWidth
        }
      : {};
  };

  // Handler for updating settings (playback speed)
  const setGameSettings = (newSettings: Settings) => {
    dispatch({
      type: "UPDATE_SETTINGS",
      settings: newSettings
    });
  };

  return (
    <SelectedHoleContext.Provider value={state.gamePlay}>
      <GameSettingsContext.Provider
        value={{
          settings: state.settings,
          gameStage,
          gameType: game.gameType,
          gameMode: "multi",
          setGameSettings
        }}
      >
        <ConnectionStatus game={game} playerId={playerId} currentPlayer={currentPlayer} />
        <div css={boardContainerStyle} data-testid="board-container">
          <Board
            dispatch={canMove ? dispatch : () => {}} // Block moves if not this player's turn
            gameType={game.gameType}
            store={state.store}
            startingSide={game.settings.startingSide}
            gameState={gameState}
            activeSide={currentSequenceSide}
            currentPlayer={currentPlayer}
            activeIndex={activeIndex}
            idle={idle}
          >
            {gameOver ? <GameOverModal won={won} isMultiplayer={true} /> : null}
          </Board>
        </div>
        {/* Router moved outside boardContainerStyle to prevent orientation transform inheritance */}
        <Router>
          <SettingsFormOverlay path="settings" />
          <HistoryOverlay path="history" moveHistory={state.moveHistory} playerId={playerId} game={game} />
        </Router>
      </GameSettingsContext.Provider>
    </SelectedHoleContext.Provider>
  );
};

export default MultiplayerBoardContainer;
